import React, { useState, useMemo, useReducer } from "react";
import { ModalV2, FullscreenTakeover, Flex, Text, StepIndicator } from "@jsq/ds/core";
import { fullScreenTakeOverBodyStyles } from "../PayeeWorkflows/styles_vanilla.css";
import { PayableDocumentUpload } from "./components/PayableDocumentUpload";
import { PayableActionTypes } from "./Context/constants";
import {
  payableDataReducer,
  initialPayableState,
  PayableDataContextProvider,
  PayableDataDispatchProvider,
  payableStepReducer,
  initialWorkflowState,
  PayableWorkflowContextProvider,
  PayableWorkflowDispatchProvider,
} from "./Context/PayableContext";
import { ClientStep } from "./StepComponents/ClientStep";
import { PaymentDetailsStep } from "./StepComponents/PaymentDetailsStep";
import { StepNames, PayableWorkflowStepConfig } from "./StepComponents/types";
import { PayableWorkflowProps, PayableDocument } from "./types";
import { getHeaderStepStatus } from "./utils";

export const PayableWorkflow: React.FC<PayableWorkflowProps> = ({ onExit, onComplete }) => {
  // Document upload state (before entering the workflow)
  const [uploadedDocuments, setUploadedDocuments] = useState<PayableDocument[]>([]);
  const [documentError, setDocumentError] = useState<string>("");

  // Initialize context state with uploaded documents
  const initialPayableData = useMemo(
    () => ({
      ...initialPayableState,
      invoiceDocuments: uploadedDocuments,
    }),
    [uploadedDocuments],
  );

  const [payableData, dispatch] = useReducer(payableDataReducer, initialPayableData);
  const [workflowData, dispatchWorkflow] = useReducer(payableStepReducer, initialWorkflowState);

  const handleDocumentsChange = (documents: PayableDocument[]) => {
    setUploadedDocuments(documents);
    if (documents.length > 0) {
      setDocumentError("");
      // Update context with documents
      dispatch({
        type: PayableActionTypes.SET_DOCUMENTS,
        payload: { invoiceDocuments: documents },
      });
    }
  };

  const workflowSteps: PayableWorkflowStepConfig[] = useMemo(() => {
    return [
      {
        index: 0,
        name: StepNames.Client,
        url: "0",
        status: getHeaderStepStatus(0, workflowData.stepNumber),
        component: ClientStep,
      },
      {
        index: 1,
        name: StepNames.PaymentDetails,
        url: "1",
        status: getHeaderStepStatus(1, workflowData.stepNumber),
        component: PaymentDetailsStep,
      },
    ];
  }, [workflowData.stepNumber]);

  const currentStepDetails = useMemo(
    () => workflowSteps[workflowData.stepNumber],
    [workflowData.stepNumber, workflowSteps],
  );

  const hasDocuments = uploadedDocuments && uploadedDocuments.length > 0;

  // When no documents are uploaded yet, show the original modal-based upload step
  if (!hasDocuments) {
    return (
      <ModalV2 show={true} onHide={onExit} title="Create new payment" size="medium">
        <Flex flexDirection="column" gap={16}>
          <Text variant="regular-md">
            Upload the supporting document below to create the payment
          </Text>
          <PayableDocumentUpload
            onFilesUploaded={handleDocumentsChange}
            onFilesDeleted={() => handleDocumentsChange([])}
            error={documentError}
          />
        </Flex>
      </ModalV2>
    );
  }

  const StepComponent = currentStepDetails.component;

  // Once a document exists, show the full workflow with Context providers
  return (
    <PayableDataContextProvider value={payableData}>
      <PayableDataDispatchProvider value={dispatch}>
        <PayableWorkflowContextProvider value={workflowData}>
          <PayableWorkflowDispatchProvider value={dispatchWorkflow}>
            <FullscreenTakeover>
              <FullscreenTakeover.Header title="Create new payable" onExit={onExit} />
              <FullscreenTakeover.Subheader>
                <StepIndicator disableStepLinks steps={workflowSteps} />
              </FullscreenTakeover.Subheader>
              <FullscreenTakeover.Body className={fullScreenTakeOverBodyStyles}>
                <StepComponent onComplete={onComplete} />
              </FullscreenTakeover.Body>
            </FullscreenTakeover>
          </PayableWorkflowDispatchProvider>
        </PayableWorkflowContextProvider>
      </PayableDataDispatchProvider>
    </PayableDataContextProvider>
  );
};

export default PayableWorkflow;
